import {
    ref,
    computed,
    shallowRef,
    watch,
    defineAsyncComponent,
    h,
    nextTick,
    defineComponent,
} from 'vue'
import axios from 'axios'
import { usePage, router } from '@inertiajs/vue3'

const resolveCallback = ref()

let resolver = {
    setResolveCallback: (callback) => {
        resolveCallback.value = callback
    },
    resolve: (name) => resolveCallback.value(name),
}

// Vite HMR herregistreerde deze interceptor bij elke plugin-rebuild zonder
// de vorige te ejecten — gevolg: één Inertia-response werd N keer mutated,
// page.props.message watcher fired N keer, toast verscheen N keer. We
// houden de laatst geregistreerde id bij en ejecten hem voordat we opnieuw
// registreren, zodat de stack zich niet kan opbouwen.
let inertiaModalInterceptorId = null

function preserveBackdrop() {
    if (inertiaModalInterceptorId !== null) {
        axios.interceptors.response.eject(inertiaModalInterceptorId)
    }
    inertiaModalInterceptorId = axios.interceptors.response.use(function (response) {
        if (response.headers['x-inertia-modal']) {
            let { component, props } = usePage()
            props = JSON.parse(JSON.stringify(props))
            if (typeof response.data === 'string') {
                response.data = JSON.parse(response.data)
            }
            response.data.props = { ...props, ...response.data.props }
            response.data.component = component
            response.headers['x-inertia'] = true
        }

        return response
    })
}

const plugin = {
    install(app, options) {
        resolver.setResolveCallback(options.resolve)

        preserveBackdrop()
    },
}

const modal = computed(() => usePage()?.props?.modal)
const props = computed(() => modal.value?.props)
const key = computed(() => modal.value?.key)

const componentName = ref()
const component = shallowRef()
const show = ref(false)
const vnode = ref()
const transitioning = ref(false)

router.on('before', (event) => {
    event.detail.visit.headers['X-Inertia-Modal-Key'] = key.value
    event.detail.visit.headers['X-Inertia-Modal-Redirect'] = modal.value?.redirectURL
    transitioning.value =
        !!event.detail.visit.headers['X-Inertia-Modal-Key'] ||
        (event.detail.visit.headers['X-Inertia-Partial-Component'] &&
            event.detail.visit.headers['X-Inertia-Modal-Key'])
})

router.on('finish', () => {
    transitioning.value = false
    if (!modal.value?.component) {
        close()
    }
})

const close = () => {
    show.value = false
    componentName.value = undefined
    component.value = undefined
    vnode.value = undefined
}

const resolveComponent = () => {
    if (!modal.value?.component) {
        // Only close if we are NOT transitioning.
        if (!transitioning.value) {
            return close()
        }
        // If transitioning, just wait, don't close yet.
        return
    }

    if (componentName.value !== modal.value?.component) {
        componentName.value = modal.value.component

        if (componentName.value) {
            component.value = defineAsyncComponent(async () => {
                try {
                    return await resolver.resolve(componentName.value)
                } catch (error) {
                    // Na een nieuwe deployment hebben Vite chunks nieuwe content hashes.
                    // Gebruikers met gecachede pagina's proberen de oude (niet meer bestaande)
                    // chunk bestanden te laden — dit geeft een TypeError.
                    // We herladen de pagina eenmalig zodat de nieuwe chunks worden geladen.
                    // Infinite reload loop wordt voorkomen via sessionStorage.
                    // Zie Sentry #74158174.
                    const reloadKey = 'modal-chunk-reload-attempted'
                    if (!sessionStorage.getItem(reloadKey)) {
                        sessionStorage.setItem(reloadKey, '1')
                        window.location.reload()
                        return
                    }
                    throw error
                }
            })
        } else {
            component.value = false
        }
    }

    vnode.value = component.value
        ? h(component.value, {
              key: key.value,
              ...props.value,
          })
        : ''

    // Ensure show is set to true only after vnode is ready
    if (vnode.value) {
        nextTick(() => (show.value = true))
    } else if (!transitioning.value) {
        // If no vnode and not transitioning, close.
        close()
    }
}

watch(modal, resolveComponent, {
    deep: true,
    immediate: true,
})

/**
 * @param {import('@inertiajs/core').VisitOptions} options
 */
const redirect = (options = {}) => {
    const redirectURL = modal.value?.redirectURL

    vnode.value = false

    if (!redirectURL) {
        return
    }

    return router.visit(redirectURL, options)
}

const useModal = () => {
    return {
        show,
        vnode,
        close,
        redirect,
        props,
    }
}

const Modal = defineComponent({
    props: ['show'],
    setup() {
        const { vnode } = useModal()
        return () => vnode.value
    },
})

export { Modal, plugin as modal, useModal, show }
